import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SopAttachmentsList } from "./sop-attachments";
import { FileText, HelpCircle, Paperclip } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export function SopViewerDialog({ open, onOpenChange, sopId }: { open: boolean, onOpenChange: (open: boolean) => void, sopId: string | null }) {
  const [activeTab, setActiveTab] = useState("content");

  const { data: sop, isLoading } = useQuery<any>({
    queryKey: ["/api/sops", sopId],
    enabled: !!sopId && open,
  });

  const editor = useEditor({
    editable: false,
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: 'prose prose-sm dark:prose-invert max-w-none',
      },
    },
  });

  useEffect(() => {
    if (open && sop && editor) {
      if (sop.content) {
        editor.commands.setContent(sop.content);
      } else if (sop.plainContent) {
        editor.commands.setContent(sop.plainContent);
      } else {
        editor.commands.setContent("");
      }
      setActiveTab("content");
    }
  }, [open, sop, editor]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-hidden flex flex-col p-0">
        <div className="flex items-center justify-between border-b px-6 py-4 bg-background">
          <div className="flex flex-col">
            <DialogTitle className="text-xl flex items-center gap-3">
              {sop?.title || "SOP Document"}
              {sop?.category && <Badge variant="secondary">{sop.category}</Badge>}
            </DialogTitle>
            <DialogDescription>
              {sop?.audience === "manus" ? "For AI Assistant" : sop?.audience === "humans" ? "For Human Assistants" : "For All Assistants"}
            </DialogDescription>
          </div>
        </div>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col md:flex-row bg-muted/20">
            {/* Tabs Sidebar */}
            <div className="w-full md:w-48 lg:w-64 border-r bg-background p-4 flex flex-col gap-2 shrink-0">
              <Button 
                variant={activeTab === "content" ? "secondary" : "ghost"} 
                className="justify-start w-full"
                onClick={() => setActiveTab("content")}
              >
                <FileText className="w-4 h-4 mr-2" /> Content
              </Button>
              {(sop?.faqs?.length || 0) > 0 && (
                <Button 
                  variant={activeTab === "faqs" ? "secondary" : "ghost"} 
                  className="justify-start w-full"
                  onClick={() => setActiveTab("faqs")}
                >
                  <HelpCircle className="w-4 h-4 mr-2" /> FAQs ({(sop.faqs || []).length})
                </Button>
              )}
              <Button 
                variant={activeTab === "attachments" ? "secondary" : "ghost"} 
                className="justify-start w-full"
                onClick={() => setActiveTab("attachments")}
              >
                <Paperclip className="w-4 h-4 mr-2" /> Attachments
              </Button>
            </div>

            {/* Tab Content */}
            <ScrollArea className="flex-1 h-full p-6 bg-background rounded-tl-xl md:rounded-none md:border-l md:border-t-0 shadow-inner">
              <div className="max-w-4xl mx-auto pb-20">
                {activeTab === "content" && (
                  <div className="space-y-6">
                    <EditorContent editor={editor} />
                  </div>
                )}

                {activeTab === "faqs" && (
                  <div className="space-y-6">
                    <h3 className="text-xl font-bold mb-4">Frequently Asked Questions</h3>
                    <div className="space-y-4">
                      {(sop?.faqs || []).map((faq: any) => (
                        <div key={faq.id} className="p-4 bg-muted/30 border rounded-lg">
                          <h4 className="font-semibold mb-2">{faq.question}</h4>
                          <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">{faq.answer}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === "attachments" && sopId && (
                  <div className="space-y-6">
                    <h3 className="text-xl font-bold mb-4">Documents & Links</h3>
                    <SopAttachmentsList sopId={sopId} readOnly={true} />
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
